import { useState } from "react";
import { freightClass } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Boxes, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { NMFC_CLASSES } from "@/constants/options";

const Num = ({ label, field, form, set, testid }) => (
  <div>
    <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</Label>
    <Input type="number" data-testid={testid} className="h-9 mt-1 font-mono-data"
      value={form[field] ?? ""} onChange={(e) => set(field, e.target.value === "" ? "" : Number(e.target.value))} />
  </div>
);

export const FreightClassField = ({ form, set }) => {
  const [busy, setBusy] = useState(false);
  const [density, setDensity] = useState(null);

  const suggest = async () => {
    setBusy(true);
    try {
      const r = await freightClass({
        length_in: Number(form.length_in || 0), width_in: Number(form.width_in || 0),
        height_in: Number(form.height_in || 0), weight_lbs: Number(form.weight_lbs || 0),
      });
      setDensity(r.density);
      set("freight_class", r.suggested_class);
      set("freight_class_source", r.density ? `auto (density ${r.density} pcf)` : "default");
      toast.success(r.density ? `Density ${r.density} pcf → Class ${r.suggested_class}` : r.note);
    } catch (e) { toast.error("Could not calculate class"); }
    finally { setBusy(false); }
  };

  return (
    <div className="sm:col-span-2 rounded-md border border-border/60 bg-neutral-50/60 p-3 space-y-3" data-testid="freight-class-field">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-neutral-700">
        <Boxes className="h-4 w-4 text-[#0055FF]" /> NMFC Freight Class
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Num label="Length (in)" field="length_in" form={form} set={set} testid="freight-length" />
        <Num label="Width (in)" field="width_in" form={form} set={set} testid="freight-width" />
        <Num label="Height (in)" field="height_in" form={form} set={set} testid="freight-height" />
        <Num label="Weight (lb)" field="weight_lbs" form={form} set={set} testid="freight-weight" />
      </div>
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Freight Class</Label>
          <div className="mt-1">
            <Select value={form.freight_class || "50"} onValueChange={(v) => { set("freight_class", v); set("freight_class_source", "manual"); }}>
              <SelectTrigger className="h-9" data-testid="freight-class-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                {NMFC_CLASSES.map((c) => <SelectItem key={c} value={c} data-testid={`freight-class-opt-${c}`}>Class {c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button type="button" variant="outline" className="h-9 gap-1.5" data-testid="suggest-class-btn" onClick={suggest} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-amber-500" />} Suggest
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Default is Class 50. Enter dimensions & weight, then Suggest to auto-populate from density — or select manually.
        {form.freight_class_source && <span className="ml-1 text-neutral-500">({form.freight_class_source})</span>}
      </p>
    </div>
  );
};
